import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { getSelectedCountry } from '@/selectors/data';
import {
  getMerchantAwareTranslator,
  getMerchantCountry,
  getPricingAnnex,
} from '@/selectors/merchant';

import { withResponsive } from 'mage-components';
import { ProductPricing } from 'moet-core';
import {
  SpacerVertical,
  ParagraphTertiary,
  Typography,
  ParagraphQuaternary,
} from '@klarna/bubble-ui';

import { KlarnaLogo } from './KlarnaLogo';
import { ProductCardDetailsExpanded } from './ProductCardDetailsExpanded';

const Card = styled.div`
  box-sizing: border-box;
  box-shadow: unset;
  border: $1px solid #f0f1f1;
  border-radius: 8px;
  background-color: #f0f1f1;

  width: 100%;
  min-width: 260px;

  position: relative;
`;

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

export const ProductCardExpanded = ({
  extraAttributesToProductFn,
  isMobile,
  product,
  tMerchantAware,
  countryKey,
  isSelectedFn,
  onChange,
  pricingAnnex,
}) => {
  // Make sure the only available product is selected on render
  useEffect(() => {
    if (!isSelectedFn(product.id)) {
      onChange(product.id);
    }
  }, []);

  const tPrefix = `products-fe.products.${product.id}`;

  const pricing = product.prices?.find(price => price.consumerCountry === countryKey);

  return (
    <Card mobile={isMobile}>
      <Container {...extraAttributesToProductFn(product)}>
        <Row>
          <TitleContainer>
            <KlarnaLogo type={product.id === 'kp_one' ? 'pink' : 'black'} />
            <Typography textToken="text-style/headings/blocks/bold/grande">
              {tMerchantAware(`${tPrefix}.name`)}
            </Typography>
          </TitleContainer>
        </Row>

        <SpacerVertical small />

        <Row>
          <ParagraphTertiary correctFootprint>
            {tMerchantAware(`${tPrefix}.description_expanded`)}
          </ParagraphTertiary>
        </Row>

        <SpacerVertical medium />

        <Row>
          {pricing ? (
            <ProductPricing
              price={pricing}
              pricingAnnex={pricingAnnex?.[product.id]}
              small
              badge
              showCrossBorderNotice={!isMobile}
            />
          ) : (
            <SpacerVertical small />
          )}
        </Row>
        {isMobile && pricing && (
          <ParagraphQuaternary>
            {tMerchantAware(
              `product-selection-container.products.products_block.cross_border_notice`,
            )}
          </ParagraphQuaternary>
        )}

        <SpacerVertical extraSmall />

        <Row>
          <ProductCardDetailsExpanded disabled={!product.isCompatible} product={product} />
        </Row>
      </Container>
    </Card>
  );
};

ProductCardExpanded.propTypes = {
  countryKey: PropTypes.string.isRequired,
  extraAttributesToProductFn: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isSelectedFn: PropTypes.func.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isCompatible: PropTypes.bool.isRequired,
    isCountryCompatible: PropTypes.bool.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        prices: PropTypes.arrayOf(
          PropTypes.shape({
            transactionFee: PropTypes.number.isRequired,
            transactionPercentageFee: PropTypes.number.isRequired,
          }),
        ),
      }),
    ).isRequired,
    pricing: PropTypes.string.isRequired,
    prices: PropTypes.arrayOf(
      PropTypes.shape({
        transactionFee: PropTypes.number.isRequired,
        transactionPercentageFee: PropTypes.number.isRequired,
      }),
    ),
  }).isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  pricingAnnex: PropTypes.objectOf(PropTypes.string),
};

export default compose(
  withResponsive,
  connect(state => ({
    tMerchantAware: getMerchantAwareTranslator(state),
    countryKey: getSelectedCountry(state)?.key || getMerchantCountry(state)?.key,
    pricingAnnex: getPricingAnnex(state),
  })),
)(ProductCardExpanded);
